import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CustomPagination from '../../../components/forms/Pagination';

import { useTranslation } from 'react-i18next';
import { Box, SvgIcon, TableSortLabel, Tooltip, Typography, useTheme } from '@mui/material';
import { GetTransfersResponse } from '../../../app/services/api/generated';
import { useNavigate } from 'react-router-dom';
import { formatDateString } from '../../../utils';
import { ArrowLeft, ArrowRight, Check, X } from '../../../assets';
import { FC } from 'react';
import RoundAndFormatValue from '../../../components/shared/RoundAndFormatValue';
import { StockTransfersTab } from '../enums/StockTransfersTab';
import useCurrencyCodeConfiguration from '../../../hooks/configurations/useCurrencyCodeConfiguration';
import { EmptyTable } from '../../../components/shared/EmptyTable';
import { Pill } from '../../../components/shared/Pill';
import { TableLoading } from '../../../components/forms/TableLoading';
import { EmptyRows } from '../../../components/forms/EmptyRows';
import { TableHeader } from '../../../components/forms/TableHeader';

type Props = {
  list: GetTransfersResponse;
  activeTab: StockTransfersTab;
  order: 'asc' | 'desc';
  setOrder: React.Dispatch<React.SetStateAction<'asc' | 'desc'>>;
  orderBy: string;
  setOrderBy: React.Dispatch<React.SetStateAction<string>>;
  loading: boolean;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  transferNumberSearch: number | undefined;
  clearAll: () => void;
  handleConfirmModal: () => Promise<void>;
}

const StockTransfersTempTable: FC<Props> = ({ list, activeTab, order, setOrder, orderBy, setOrderBy, loading, page, setPage, transferNumberSearch, clearAll, handleConfirmModal }) => {
  const { t } = useTranslation('stockTransfers');
  const navigate = useNavigate();
  const theme = useTheme();
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const rowsPerPage = 10;
  const maxPages = list?.totalCount
    ? Math.max(1, Math.ceil(list.totalCount / rowsPerPage))
    : 1;

  const isAsc = order === 'asc';

  const handleSort = (property: string) => {
    const order = isAsc ? 'desc' : 'asc';
    setOrder(order);
    setOrderBy(property);
  };

  if (list.totalCount == 0 && !loading) {
    return <EmptyTable bodyRowHeight={40}
      headerHeight={45}
      text={t('title')}
      searchTerm={transferNumberSearch?.toString()}
      onClear={clearAll}
      onAdd={handleConfirmModal}
      onAddIsCreating={false}
      addItemText={t('create')}
      onAddIsDisabled={false} />;
  }

  return (
    <>
      <TableHeader>
        {t('title')}
        {(list?.totalCount ?? 0) > 0 && <Pill text={`${list.totalCount} ${t('title')}`}></Pill>}
      </TableHeader>
      {loading ? <TableLoading headerHeight={45}
        bodyRowHeight={40}
        minTableWidth={1100} /> :
        <TableContainer>
          <Table sx={{ minWidth: '1100px' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel
                    active={orderBy === 'id'}
                    direction={order}
                    onClick={() => handleSort('id')}
                  >
                    {t('table.header.transfer_number')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel
                    active={orderBy === 'storeName'}
                    direction={order}
                    onClick={() => handleSort('storeName')}
                  >
                    {t('table.header.store')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel
                    active={orderBy === 'numberOfItems'}
                    direction={order}
                    onClick={() => handleSort('numberOfItems')}
                  >
                    {t('table.header.number_items')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel
                    active={orderBy === 'requestedValue'}
                    direction={order}
                    onClick={() => handleSort('requestedValue')}
                  >
                    {t('table.header.req_value')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel
                    active={orderBy === 'receivedValue'}
                    direction={order}
                    onClick={() => handleSort('receivedValue')}
                  >
                    {t('table.header.received_value')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel
                    active={orderBy === 'transferType'}
                    direction={order}
                    onClick={() => handleSort('transferType')}
                  >
                    {t('table.header.type')}
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <TableSortLabel
                    active={orderBy === 'transferDate'}
                    direction={order}
                    onClick={() => handleSort('transferDate')}
                  >
                    {t('table.header.requested_date')}
                  </TableSortLabel>
                </TableCell>
                {
                  activeTab === StockTransfersTab.ARCHIVED &&
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <TableSortLabel
                      active={orderBy === 'acceptedDate'}
                      direction={order}
                      onClick={() => handleSort('acceptedDate')}
                    >
                      {t('table.header.accepted_date')}
                    </TableSortLabel>
                  </TableCell>
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {list?.transfers?.map((row) => (
                <TableRow
                  key={row.id}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/store/stock-transfer/${row.id}`)}
                >
                  <TableCell>
                    {activeTab === StockTransfersTab.ARCHIVED && row.isDeleted &&
                      <Box display="flex"
                        alignItems='center'>
                        <Tooltip title={t('table.deletedTooltip')}>
                          <SvgIcon sx={{ mr: '5px' }}> <X color={theme.palette.error[500]} /> </SvgIcon>
                        </Tooltip>
                        <Typography variant='textXS'
                          fontWeight={600}
                          color={theme.palette.primary[500]}
                          sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                          {row.id}
                        </Typography>
                      </Box>
                    }
                    {activeTab === StockTransfersTab.ARCHIVED && !row.isDeleted &&
                      <Box display="flex"
                        alignItems='center'>
                        <Tooltip title={t('table.approvedTooltip')}>
                          <SvgIcon sx={{ mr: '5px' }}> <Check color={theme.palette.primary[500]} /> </SvgIcon>
                        </Tooltip>
                        <Typography variant='textXS'
                          fontWeight={600}
                          color={theme.palette.primary[500]}
                          sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                          {row.id}
                        </Typography>
                      </Box>
                    }
                    {activeTab === StockTransfersTab.ACTIVE && <Typography variant='textXS'
                      fontWeight={600}
                      color={theme.palette.primary[500]}
                      sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      {row.id}
                    </Typography>}
                  </TableCell>
                  <TableCell variant='clampedText'>
                    <Typography variant='textXS'>
                      {row.storeName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {row.numberOfItems}
                  </TableCell>
                  <TableCell>
                    {<RoundAndFormatValue value={row.requestedValue ?? 0}
                      currencyCode={currencyCode} />}
                  </TableCell>
                  <TableCell>
                    {row.receivedValue !== null &&
                      <RoundAndFormatValue value={row.receivedValue ?? 0}
                        currencyCode={currencyCode} />}
                  </TableCell>
                  <TableCell>
                    {row.transferType === 'Incoming' &&
                      <Box sx={{ display: 'flex', alignItems: 'center', maxHeight: '40px' }}>
                        <Tooltip title={t('table.incomingTooltip')}>
                          <SvgIcon color='success'
                            sx={{ mr: '5px', height: '20px' }}> <ArrowLeft /> </SvgIcon>
                        </Tooltip>
                        <Typography variant='textXS'>
                          {row.transferType}
                        </Typography>
                      </Box>
                    }
                    {row.transferType === 'Outgoing' &&
                      <Box sx={{ display: 'flex', alignItems: 'center', maxHeight: '40px' }}>
                        <Tooltip title={t('table.incomingTooltip')}>
                          <SvgIcon color='success'
                            sx={{ mr: '5px', height: '20px' }}> <ArrowRight /> </SvgIcon>
                        </Tooltip>
                        <Typography variant='textXS'>
                          {row.transferType}
                        </Typography>
                      </Box>
                    }
                  </TableCell>
                  <TableCell>
                    {row.transferDate && formatDateString(row.transferDate)}
                  </TableCell>
                  {activeTab === StockTransfersTab.ARCHIVED &&
                    <TableCell>
                      {row.acceptedDate && formatDateString(row.acceptedDate)}
                    </TableCell>
                  }
                </TableRow>
              ))}

              <EmptyRows numberOfCurrentRows={list?.transfers?.length ?? 0}
                height={40} />
            </TableBody>
          </Table>
        </TableContainer >}
      <CustomPagination useNoDataMessage={false}
        page={page}
        setPage={setPage}
        maxPages={maxPages}
        breakpointForChangeDisplay={120000}
      />
    </>
  );
};

export default StockTransfersTempTable;
